// SharedPickupStore: single source of truth for simple chef↔waiter demo
// Keeps a tiny static dataset of pickup tickets and lets both pages subscribe.

const listeners = new Set();
// Simple LIFO history stack to support Undo of the last delivery action (global)
let history = [];
// Per-ticket and per-table history stacks for targeted undos
const ticketHistory = {}; // ticketId -> [ticketSnapshot]
const tableHistory = {};  // tableNumber -> [ticketSnapshot]

function deepCloneTicket(t) {
  return {
    ...t,
    completedAt: t?.completedAt ? new Date(t.completedAt) : undefined,
    pickedUpAt: t?.pickedUpAt ? new Date(t.pickedUpAt) : undefined,
    items: (t.items || []).map(i => ({ ...i }))
  };
}

// Small fixed dataset, shared across Chef and Waiter
let tickets = [
  {
    id: 'pickup-1',
    orderIds: ['order-101'],
    items: [
      { id: 'pickup-1-item-1', name: 'Chicken Biryani', quantity: 2, status: 'ready_on_pass' },
      { id: 'pickup-1-item-2', name: 'Raita', quantity: 1, status: 'ready_on_pass' }
    ],
    tableNumber: 3,
    kitchenSection: 'Hot Kitchen',
    completedAt: new Date(Date.now() - 6 * 60000),
    queueStatus: 'ready'
  },
  {
    id: 'pickup-2',
    orderIds: ['order-102'],
    items: [
      { id: 'pickup-2-item-1', name: 'Paneer Butter Masala', quantity: 1, status: 'ready_on_pass' },
      { id: 'pickup-2-item-2', name: 'Butter Naan', quantity: 10, status: 'ready_on_pass' }
    ],
    tableNumber: 7,
    kitchenSection: 'Grill',
    completedAt: new Date(Date.now() - 3 * 60000),
    queueStatus: 'ready'
  }
];

function emit() {
  const snapshot = tickets.map(t => ({
    ...t,
    items: (t.items || []).map(i => ({ ...i }))
  }));
  listeners.forEach(fn => {
    try { fn(snapshot); } catch (_) {}
  });
}

export function subscribeToTickets(handler) {
  if (typeof handler === 'function') listeners.add(handler);
  // emit current state immediately for convenience
  try { handler && handler(getTickets()); } catch (_) {}
  return () => listeners.delete(handler);
}

export function getTickets() {
  return tickets.map(t => ({ ...t, items: (t.items || []).map(i => ({ ...i })) }));
}

export function addTicket({ dishName, quantity = 1, tableNumber = 99, kitchenSection = 'Hot Kitchen' }) {
  const now = new Date();
  const id = `pickup-${now.getTime()}-${Math.floor(Math.random()*1000)}`;
  const t = {
    id,
    orderIds: [],
    items: [ { id: `${id}-item-1`, name: dishName, quantity, status: 'ready_on_pass' } ],
    tableNumber,
    kitchenSection,
    completedAt: now,
    queueStatus: 'ready'
  };
  tickets = [...tickets, t];
  emit();
  return id;
}

export function pickupTicket(ticketId) {
  tickets = tickets.map(t => {
    if (t.id !== ticketId) return t;
    const items = (t.items || []).map(i => i.status === 'ready_on_pass' ? { ...i, status: 'in_hand' } : i);
    return { ...t, items, pickedUpAt: new Date() };
  });
  emit();
}

export function deliverItems(ticketId, selectedItems = []) {
  // Take a deep snapshot for global undo
  const snapshot = tickets.map(t => deepCloneTicket(t));
  history.push(snapshot);
  if (history.length > 25) history.shift();
  
  // Take a per-ticket and per-table snapshot (only the affected ticket)
  const prevTicket = tickets.find(t => t.id === ticketId);
  if (prevTicket) {
    const prevClone = deepCloneTicket(prevTicket);
    if (!ticketHistory[ticketId]) ticketHistory[ticketId] = [];
    ticketHistory[ticketId].push(prevClone);
    if (ticketHistory[ticketId].length > 50) ticketHistory[ticketId].shift();
    const tn = prevTicket.tableNumber;
    if (tn != null) {
      if (!tableHistory[tn]) tableHistory[tn] = [];
      tableHistory[tn].push(prevClone);
      if (tableHistory[tn].length > 50) tableHistory[tn].shift();
    }
  }

  // Extract orderId from ticketId (format: pickup-{orderId})
  const orderId = prevTicket?.orderId || ticketId.replace('pickup-', '');
  
  // Get portion IDs from selected items and map them with quantities
  const itemsWithPortionIds = selectedItems
    .map(s => {
      const item = (prevTicket?.items || []).find(i => {
        const match = i.id === s.id || i.portion_id === s.id || i.portion_id === s.portion_id || i.id === s.portion_id;
        return match;
      });
      if (!item) {
        console.warn(`⚠️ Could not find item for id: ${s.id}, portion_id: ${s.portion_id}`);
        return null;
      }
      return {
        id: item.portion_id,
        portion_id: item.portion_id,
        quantity: s.quantity || 1
      };
    })
    .filter(Boolean);
  

  // Make API call to backend to mark items as served
  const API_BASE = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  
  const requestBody = {
    orderId: orderId,
    selectedItems: itemsWithPortionIds // Send selected items with portion_ids and quantities
  };
  
  
  fetch(`${API_BASE}/waiter/deliver`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestBody)
  })
  .then(res => res.json())
  .then(data => {
    console.log('✅ Deliver API response:', data);
  })
  .catch(err => {
    console.error('❌ Error calling deliver API:', err);
  });

  // Update local state optimistically
  tickets = tickets.map(t => {
    if (t.id !== ticketId) return t;
    const items = (t.items || []).map(item => {
      const selected = selectedItems.find(s => s.id === item.id);
      if (!selected) return item;
      
      const deliveredQty = selected.quantity || 1;
      const remainingQty = Math.max(0, item.quantity - deliveredQty);
      
      if (remainingQty === 0) {
        // Mark as served if fully delivered
        return { ...item, status: 'served' };
      } else {
        // Keep remaining quantity
        return { ...item, quantity: remainingQty };
      }
    });
    
    // Add served portions as separate items for tracking
    const servedItems = selectedItems
      .filter(s => (t.items || []).find(item => item.id === s.id))
      .map(s => {
        const originalItem = (t.items || []).find(item => item.id === s.id);
        return {
          id: `${s.id}-served-${Date.now()}`,
          name: originalItem.name || originalItem.menu_item_name,
          quantity: s.quantity,
          status: 'served'
        };
      });
    
    return { ...t, items: [...items, ...servedItems] };
  });
  
  // Remove tickets that are fully served (all original items have quantity 0 or status served)
  tickets = tickets.filter(t => {
    const originalItems = (t.items || []).filter(i => !i.id.includes('-served-'));
    return originalItems.some(i => i.status !== 'served' && i.quantity > 0);
  });
  
  emit();
}

export function undoLastDelivery() {
  if (history.length === 0) return false;
  const snapshot = history.pop();
  tickets = snapshot.map(t => deepCloneTicket(t));
  emit();
  return true;
}

export function canUndo() {
  return history.length > 0;
}

export function undoLastDeliveryForTicket(ticketId) {
  const stack = ticketHistory[ticketId];
  if (!stack || stack.length === 0) return false;
  const restored = deepCloneTicket(stack.pop());
  let replaced = false;
  tickets = tickets.map(t => {
    if (t.id === restored.id) { replaced = true; return restored; }
    return t;
  });
  if (!replaced) {
    tickets = [...tickets, restored];
  }
  emit();
  return true;
}

export function canUndoForTicket(ticketId) {
  const stack = ticketHistory[ticketId];
  return !!(stack && stack.length > 0);
}

export function undoLastDeliveryForTable(tableNumber) {
  const stack = tableHistory[tableNumber];
  if (!stack || stack.length === 0) return false;
  const restored = deepCloneTicket(stack.pop());
  let replaced = false;
  tickets = tickets.map(t => {
    if (t.id === restored.id) { replaced = true; return restored; }
    return t;
  });
  if (!replaced) {
    tickets = [...tickets, restored];
  }
  emit();
  return true;
}

export function canUndoForTable(tableNumber) {
  const stack = tableHistory[tableNumber];
  return !!(stack && stack.length > 0);
}

// Legacy compatibility exports from KitchenWaiterLink.js
export function subscribeToReadyTickets(handler) {
  // Map to the new subscription system
  return subscribeToTickets(handler);
}

export function publishReadyTicket(ticket) {
  // Map to the new ticket system - add the ticket directly
  tickets = [...tickets, ticket];
  emit();
}

export function createReadyTicket({ dishName, quantity = 1, tableNumber = 99, kitchenSection = 'KDS' }) {
  const now = new Date();
  const id = `pickup-${now.getTime()}-${Math.floor(Math.random()*1000)}`;
  return {
    id,
    orderIds: [],
    items: [
      { id: `${id}-item-1`, name: dishName, quantity, status: 'ready_on_pass' }
    ],
    tableNumber,
    kitchenSection,
    completedAt: now,
    queueStatus: 'ready'
  };
}
